import type {
  ProfileMappingCandidate,
  ProfileMappingMode,
  ProfileMappingPart,
  ProfileMappingRule,
  ProviderProfileMappings,
  SourcePropertyDefinition,
  TargetFieldDefinition,
} from './types';

export const separatorOptions = [
  { value: '', label: '붙여쓰기' },
  { value: ' ', label: '공백' },
  { value: ' · ', label: '가운데점 ( · )' },
  { value: ' / ', label: '슬래시 ( / )' },
  { value: '-', label: '하이픈 (-)' },
  { value: '_', label: '밑줄 (_)' },
] as const;

const mappingModes: ProfileMappingMode[] = ['FIRST_NON_EMPTY', 'JOIN_NON_EMPTY', 'FALLBACK_CHAIN'];

function cloneCandidate(candidate: ProfileMappingCandidate): ProfileMappingCandidate {
  return {
    ...candidate,
    sourceProperties: [...candidate.sourceProperties],
    ...(candidate.parts == null ? {} : { parts: candidate.parts.map((part) => ({ ...part })) }),
  };
}

export function candidateParts(candidate: ProfileMappingCandidate): ProfileMappingPart[] {
  if (candidate.parts != null) return candidate.parts.map((part) => ({ ...part }));
  return candidate.sourceProperties.flatMap((sourceProperty, index) => [
    ...(index > 0 && candidate.separator ? [{ text: candidate.separator }] : []),
    { sourceProperty },
  ]);
}

export function candidateFromParts(parts: ProfileMappingPart[], requireAll?: boolean): ProfileMappingCandidate {
  return {
    sourceProperties: [],
    parts: parts.map((part) => ({ ...part })),
    ...(requireAll === undefined ? {} : { requireAll }),
  };
}

export function candidateFormatSegments(candidate: ProfileMappingCandidate): { sourceProperties: string[]; textGaps: string[] } {
  const sourceProperties: string[] = [];
  const textGaps = [''];
  for (const part of candidateParts(candidate)) {
    if (part.sourceProperty) {
      sourceProperties.push(part.sourceProperty);
      textGaps.push('');
    } else {
      textGaps[textGaps.length - 1] += part.text ?? '';
    }
  }
  return { sourceProperties, textGaps };
}

export function candidateFromFormatSegments(
  sourceProperties: string[],
  textGaps: string[],
  requireAll = true,
): ProfileMappingCandidate {
  const parts: ProfileMappingPart[] = [];
  for (let index = 0; index <= sourceProperties.length; index += 1) {
    const characters = Array.from(textGaps[index] ?? '');
    for (let offset = 0; offset < characters.length; offset += 80) {
      parts.push({ text: characters.slice(offset, offset + 80).join('') });
    }
    if (index < sourceProperties.length) parts.push({ sourceProperty: sourceProperties[index] });
  }
  return candidateFromParts(parts, requireAll);
}

export function insertFormatSource(
  candidate: ProfileMappingCandidate,
  sourceProperty: string,
  gapIndex: number,
  textOffset: number,
  textSelectionEnd = textOffset,
): ProfileMappingCandidate {
  const { sourceProperties, textGaps } = candidateFormatSegments(candidate);
  const insertionIndex = Math.max(0, Math.min(gapIndex, sourceProperties.length));
  const text = textGaps[insertionIndex];
  const caret = Math.max(0, Math.min(textOffset, text.length));
  const selectionEnd = Math.max(caret, Math.min(textSelectionEnd, text.length));
  sourceProperties.splice(insertionIndex, 0, sourceProperty);
  textGaps.splice(insertionIndex, 1, text.slice(0, caret), text.slice(selectionEnd));
  return candidateFromFormatSegments(sourceProperties, textGaps, candidate.parts != null ? candidate.requireAll ?? false : true);
}

function candidateSourceKeys(candidate: ProfileMappingCandidate): string[] {
  return candidate.parts == null
    ? candidate.sourceProperties
    : candidate.parts.flatMap((part) => part.sourceProperty ? [part.sourceProperty] : []);
}

export function cloneMappingRule(rule: ProfileMappingRule): ProfileMappingRule {
  return {
    ...rule,
    sourceProperties: [...rule.sourceProperties],
    ...(rule.candidates === undefined ? {} : { candidates: rule.candidates.map(cloneCandidate) }),
  };
}

export function mappingCandidates(rule: ProfileMappingRule): ProfileMappingCandidate[] {
  if (rule.mode === 'FALLBACK_CHAIN') {
    return (rule.candidates ?? []).map((candidate) => ({
      sourceProperties: [...candidate.sourceProperties],
      separator: candidate.separator ?? '',
      requireAll: candidate.requireAll ?? false,
      ...(candidate.parts == null ? {} : { parts: candidate.parts.map((part) => ({ ...part })) }),
    }));
  }
  if (rule.sourceProperties.length === 0) return [];
  if (rule.mode === 'JOIN_NON_EMPTY') {
    return [{ sourceProperties: [...rule.sourceProperties], separator: rule.separator ?? '', requireAll: false }];
  }
  return rule.sourceProperties.map((source) => ({ sourceProperties: [source], separator: '', requireAll: false }));
}

export function mappingFromCandidates(targetField: string, candidates: ProfileMappingCandidate[]): ProfileMappingRule {
  const singletonSources = candidates.map((candidate) => candidate.sourceProperties[0]);
  if (candidates.every((candidate) => candidate.parts == null && candidate.sourceProperties.length === 1)
    && new Set(singletonSources).size === singletonSources.length) {
    return { targetField, mode: 'FIRST_NON_EMPTY', sourceProperties: singletonSources };
  }
  if (candidates.length === 1 && candidates[0].parts == null && candidates[0].sourceProperties.length > 1 && !candidates[0].requireAll) {
    return {
      targetField,
      mode: 'JOIN_NON_EMPTY',
      sourceProperties: [...candidates[0].sourceProperties],
      separator: candidates[0].separator ?? '',
    };
  }
  return {
    targetField,
    mode: 'FALLBACK_CHAIN',
    sourceProperties: [],
    candidates: candidates.map((candidate) => ({
      sourceProperties: [...candidate.sourceProperties],
      ...(candidate.separator ? { separator: candidate.separator } : {}),
      ...(candidate.requireAll ? { requireAll: true } : {}),
      ...(candidate.parts == null ? {} : { parts: candidate.parts.map((part) => ({ ...part })) }),
    })),
  };
}

export function mappingSourceKeys(rule: ProfileMappingRule): string[] {
  return [...new Set(mappingCandidates(rule).flatMap(candidateSourceKeys))];
}

export function rulesForProvider(provider: ProviderProfileMappings): ProfileMappingRule[] {
  return provider.targetFields.map((target) => {
    const existing = provider.mappings.find((mapping) => mapping.targetField === target.key);
    return existing
      ? cloneMappingRule(existing)
      : { targetField: target.key, mode: 'FIRST_NON_EMPTY', sourceProperties: [] };
  });
}

export function mappingKey(mappings: ProfileMappingRule[]) {
  return JSON.stringify(mappings.map((mapping) => ({
    targetField: mapping.targetField,
    mode: mapping.mode,
    sourceProperties: mapping.sourceProperties,
    separator: mapping.mode === 'JOIN_NON_EMPTY' ? mapping.separator ?? '' : '',
    ...(mapping.mode === 'FALLBACK_CHAIN' ? {
      candidates: mappingCandidates(mapping).map((candidate) => ({
        ...candidate,
        ...(candidate.parts == null ? {} : {
          parts: candidate.parts.map((part) => ({ sourceProperty: part.sourceProperty || undefined, text: part.text || undefined })),
        }),
      })),
    } : {}),
  })));
}

export function allowedSources(target: TargetFieldDefinition, sources: SourcePropertyDefinition[]) {
  if (target.allowedSourceProperties) {
    const allowedKeys = new Set(target.allowedSourceProperties);
    return sources.filter((source) => allowedKeys.has(source.key));
  }
  if (target.key === 'relations.manager') return sources.filter((source) => source.key === 'managerDirectoryEmail');
  if (target.key === 'custom.manager') return sources.filter((source) => source.key === 'managerSlackUserId');
  return sources.filter((source) => source.key !== 'managerSlackUserId');
}

export type MappingPreviewEntry = {
  key: string;
  label: string;
  value: string;
  status: 'used' | 'empty' | 'skipped';
};

export type MappingPreviewCandidateResult = {
  index: number;
  value: string;
  status: 'used' | 'empty' | 'skipped';
  reason?: 'required_source_empty' | 'no_value' | 'earlier_candidate_used';
  entries: MappingPreviewEntry[];
};

export function evaluateMappingPreview(
  rule: ProfileMappingRule,
  sources: SourcePropertyDefinition[],
  overrides: Record<string, string> = {},
): { value: string; entries: MappingPreviewEntry[]; candidateResults: MappingPreviewCandidateResult[] } {
  const sourceEntries = new Map(mappingSourceKeys(rule).map((key) => {
    const source = sources.find((candidate) => candidate.key === key);
    const value = (Object.hasOwn(overrides, key) ? overrides[key] : source?.example ?? '').trim();
    return [key, { key, label: source?.label ?? key, value }];
  }));
  let result = '';
  const usedKeys = new Set<string>();
  const candidateResults = mappingCandidates(rule).map((candidate, index): MappingPreviewCandidateResult => {
    const values = candidateSourceKeys(candidate).map((key) => sourceEntries.get(key)!);
    const incomplete = candidate.requireAll && values.some((entry) => !entry.value);
    const hasValue = values.some((entry) => entry.value);
    const value = incomplete || !hasValue ? '' : candidate.parts == null
      ? values.filter((entry) => entry.value).map((entry) => entry.value).join(candidate.separator ?? '')
      : candidate.parts.map((part) => part.sourceProperty ? sourceEntries.get(part.sourceProperty)?.value ?? '' : part.text ?? '').join('');
    let status: MappingPreviewCandidateResult['status'];
    let reason: MappingPreviewCandidateResult['reason'];
    if (result) {
      status = 'skipped';
      reason = 'earlier_candidate_used';
    } else if (incomplete) {
      status = 'skipped';
      reason = 'required_source_empty';
    } else if (!value) {
      status = 'empty';
      reason = 'no_value';
    } else {
      status = 'used';
      result = value;
      for (const entry of values) if (entry.value) usedKeys.add(entry.key);
    }
    return {
      index,
      value,
      status,
      ...(reason ? { reason } : {}),
      entries: values.map((entry) => ({ ...entry, status: !entry.value ? 'empty' : status === 'used' ? 'used' : 'skipped' })),
    };
  });
  return {
    value: result,
    entries: [...sourceEntries.values()].map((entry) => ({
      ...entry,
      status: !entry.value ? 'empty' : usedKeys.has(entry.key) ? 'used' : 'skipped',
    })),
    candidateResults,
  };
}

export function previewValue(rule: ProfileMappingRule, sources: SourcePropertyDefinition[]) {
  return evaluateMappingPreview(rule, sources).value;
}

export function validateMappings(provider: ProviderProfileMappings, mappings: ProfileMappingRule[]) {
  const seenTargets = new Set<string>();
  for (const rule of mappings) {
    const targetKey = rule.targetField.trim();
    if (!provider.targetFields.some((target) => target.key === targetKey)) return '지원하지 않는 연결 필드가 포함되어 있습니다.';
    if (seenTargets.has(targetKey)) return '같은 연결 필드를 중복해서 설정할 수 없습니다.';
    seenTargets.add(targetKey);
  }
  for (const target of provider.targetFields) {
    const rule = mappings.find((candidate) => candidate.targetField.trim() === target.key);
    if (!rule) return `${target.label} 매핑이 없습니다.`;
    const managerTarget = target.key === 'relations.manager' || target.key === 'custom.manager';
    const allowedModes = target.allowedModes ?? (managerTarget ? ['FIRST_NON_EMPTY'] : mappingModes);
    if (!allowedModes.includes(rule.mode)) return `${target.label} 필드에서 지원하지 않는 매핑 방식입니다.`;
    const allowedKeys = target.allowedSourceProperties ?? (managerTarget
      ? [target.key === 'relations.manager' ? 'managerDirectoryEmail' : 'managerSlackUserId']
      : undefined);
    const separators: readonly string[] = target.allowedSeparators ?? separatorOptions.map((option) => option.value);
    const sourceError = (keys: string[]) => {
      const normalized = keys.map((key) => key.trim());
      if (normalized.some((key) => !key || (allowedKeys && !allowedKeys.includes(key)))) return `${target.label} 필드에 연결할 수 없는 직원 속성이 포함되어 있습니다.`;
      if (new Set(normalized).size !== normalized.length) return `${target.label} 필드의 같은 값에는 직원 속성을 중복해서 연결할 수 없습니다.`;
      return undefined;
    };
    if (rule.mode === 'FALLBACK_CHAIN') {
      if (rule.sourceProperties.length > 0 || rule.separator) return `${target.label} 필드는 각 값에서 직원 속성과 구분자를 설정해 주세요.`;
      const candidates = rule.candidates ?? [];
      if (candidates.length < 1 || candidates.length > 6) return `${target.label} 필드에는 사용할 값과 대체값을 합쳐 1개에서 6개까지 설정해 주세요.`;
      if (candidates.reduce((total, candidate) => total + candidateSourceKeys(candidate).length, 0) > 6) return `${target.label} 필드의 모든 값에는 직원 속성을 합쳐 최대 6개까지 연결할 수 있습니다.`;
      let literalLength = 0;
      for (const [index, candidate] of candidates.entries()) {
        if (candidate.parts != null) {
          if (!target.supportsValueTemplates) return `${target.label} 필드에서는 직접 작성한 형식을 사용할 수 없습니다.`;
          if (candidate.sourceProperties.length > 0 || candidate.separator) return `${target.label} 필드의 형식에는 속성과 고정 문구만 순서대로 설정해 주세요.`;
          if (candidate.parts.length > 18) return `${target.label}의 ${index + 1}번째 값에는 속성과 고정 문구를 합쳐 최대 18개까지 넣을 수 있습니다.`;
          for (const part of candidate.parts) {
            if (Boolean(part.sourceProperty) === Boolean(part.text)) return `${target.label} 필드의 각 형식 항목에는 속성이나 고정 문구 중 하나를 넣어 주세요.`;
            if (part.text) {
              const length = Array.from(part.text).length;
              if (length > 80) return `${target.label} 필드의 고정 문구는 항목마다 최대 80자까지 입력할 수 있습니다.`;
              if (/[\p{Cc}\u2028\u2029]/u.test(part.text)) return `${target.label} 필드의 고정 문구에는 줄바꿈이나 제어 문자를 넣을 수 없습니다.`;
              literalLength += length;
              if (literalLength > 240) return `${target.label} 필드의 고정 문구는 모두 합쳐 최대 240자까지 입력할 수 있습니다.`;
            }
          }
        }
        const keys = candidateSourceKeys(candidate);
        if (keys.length === 0) return `${target.label}의 ${index + 1}번째 값에 직원 속성을 연결해 주세요.`;
        const error = sourceError(keys);
        if (error) return error;
        if (candidate.parts == null && !separators.includes(candidate.separator ?? '')) return `${target.label} 필드에서 지원하지 않는 구분자입니다.`;
      }
    } else {
      if (rule.candidates !== undefined) return `${target.label} 필드에서 지원하지 않는 대체값 설정입니다.`;
      if (target.required && rule.sourceProperties.length === 0) return `${target.label} 필드에는 직원 속성을 하나 이상 연결해 주세요.`;
      if (rule.sourceProperties.length > 6) return `${target.label} 필드에는 직원 속성을 최대 6개까지 연결할 수 있습니다.`;
      const error = sourceError(rule.sourceProperties);
      if (error) return error;
      if (rule.mode === 'JOIN_NON_EMPTY' && !separators.includes(rule.separator ?? '')) return `${target.label} 필드에서 지원하지 않는 구분자입니다.`;
    }
  }
  return undefined;
}
